import Dexie, { type Table } from "dexie";
import { buildDemoContractors, buildDemoJobs } from "../data/demoData";
import type {
  AppSettings,
  ContractorProfile,
  DraftJob,
  JobPost,
  SavedJob,
  UserProfile,
} from "../types";

export class ClearScopeDb extends Dexie {
  jobs!: Table<JobPost, string>;
  contractors!: Table<ContractorProfile, string>;
  savedJobs!: Table<SavedJob, string>;
  profiles!: Table<UserProfile, string>;
  settings!: Table<AppSettings, string>;
  drafts!: Table<DraftJob, string>;

  constructor() {
    super("clearscope-local-db");
    this.version(1).stores({
      jobs: "id, category, urgency, status, neighborhood, city, state, updatedAt",
      contractors: "id, businessName, serviceArea, verificationState",
      savedJobs: "id, jobId, savedAt",
      profiles: "id, role, createdAt",
      settings: "id, updatedAt",
    });
    this.version(2).stores({
      jobs: "id, category, urgency, status, neighborhood, city, state, updatedAt",
      contractors: "id, businessName, serviceArea, verificationState",
      savedJobs: "id, jobId, savedAt",
      profiles: "id, role, createdAt",
      settings: "id, updatedAt, lastExportAt",
      drafts: "id, updatedAt",
    });
  }
}

export const db = new ClearScopeDb();

export function defaultSettings(): AppSettings {
  return {
    id: "local",
    theme: "system",
    aiProvider: "none",
    aiDataAccess: {
      jobs: false,
      riskFlags: false,
      contact: false,
    },
    enableExperimentalLocalLlm: false,
    acknowledgedStaticLimitations: false,
    updatedAt: new Date().toISOString(),
  };
}

export async function seedDemoData(): Promise<void> {
  const jobCount = await db.jobs.count();
  const contractorCount = await db.contractors.count();

  await db.transaction("rw", [db.jobs, db.contractors, db.settings], async () => {
    if (jobCount === 0) {
      await db.jobs.bulkPut(buildDemoJobs());
    }

    if (contractorCount === 0) {
      await db.contractors.bulkPut(buildDemoContractors());
    }

    const settings = await db.settings.get("local");
    if (!settings) {
      await db.settings.put(defaultSettings());
    } else if (settings.acknowledgedStaticLimitations === undefined || !settings.aiDataAccess) {
      await db.settings.put({
        ...settings,
        acknowledgedStaticLimitations: settings.acknowledgedStaticLimitations ?? false,
        aiDataAccess: settings.aiDataAccess ?? {
          jobs: false,
          riskFlags: false,
          contact: false,
        },
        updatedAt: new Date().toISOString(),
      });
    }
  });
}

export async function requestPersistentStorage(): Promise<boolean> {
  if (!navigator.storage?.persist) {
    return false;
  }
  return navigator.storage.persist();
}

export async function clearLocalData(): Promise<void> {
  await db.transaction(
    "rw",
    [db.jobs, db.contractors, db.savedJobs, db.profiles, db.settings, db.drafts],
    async () => {
      await Promise.all([
        db.jobs.clear(),
        db.contractors.clear(),
        db.savedJobs.clear(),
        db.profiles.clear(),
        db.settings.clear(),
        db.drafts.clear(),
      ]);
    },
  );
}
